import { Chess } from "#vendor/chessjs/chess.js";
import { game } from "../state/index.ts";

export function formatYearMonth(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function normalizeYearMonth(raw) {
  return typeof raw === "string" && raw.length >= 7 ? raw.slice(0, 7) : "";
}

export function parseFullmove(fenString) {
  const parts = fenString.split(" ");
  const num = parseInt(parts[5], 10);
  return Number.isInteger(num) && num > 0 ? num : 1;
}

export function clamp(min, max, value) {
  return Math.min(max, Math.max(min, value));
}

export function shortNumber(n) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (abs >= 1_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return `${n}`;
}

export function pvUciToSan(pv, fen, maxMoves = 10) {
  if (!pv) return "";
  try {
    const pvMoves = pv.trim().split(/\s+/).filter(Boolean);
    const pvGame = new Chess(fen || game.fen());
    const sanMoves = [];
    const hasMore = pvMoves.length > maxMoves;
    const movesToProcess = pvMoves.slice(0, maxMoves);
    movesToProcess.forEach((uci) => {
      const from = uci.slice(0, 2);
      const to = uci.slice(2, 4);
      const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
      const move = pvGame.move({ from, to, promotion });
      if (move && move.san) {
        sanMoves.push(move.san);
      }
    });
    const result = sanMoves.length ? sanMoves.join(" ") : pv;
    return hasMore ? result + "..." : result;
  } catch (err) {
    return pv;
  }
}

export function buildUciHistory() {
  const verbose = game.history({ verbose: true });
  return verbose.map((mv) => {
    const promo = mv.promotion ? mv.promotion : "";
    return `${mv.from}${mv.to}${promo}`;
  });
}

export function formatScore(data, fen) {
  if (data == null) return "N/A";
  const sign = fen?.split(" ")[1] === "b" ? -1 : 1;
  if (typeof data.mate === "number") {
    const mate = data.mate * sign;
    return mate === 0 ? "M0" : `M${mate}`;
  }
  if (typeof data.scoreCp === "number") {
    const cp = data.scoreCp * sign;
    const pawns = (cp / 100).toFixed(2);
    return `${cp > 0 ? "+" : ""}${pawns}`;
  }
  return "N/A";
}

export function extractEngineScore(info, fen) {
  if (!info) return null;
  const turn = fen?.split(" ")[1];
  const sign = turn === "b" ? -1 : 1;
  if (typeof info.mate === "number") {
    // A forced mate is a huge but bounded advantage (a shorter mate is worth more). Multiplying the
    // mate distance by 100 turned "mate in 3" into +300 pawns on screen and broke every comparison.
    const winner = info.mate === 0 ? -1 : Math.sign(info.mate);
    return winner * sign * (100 - Math.min(99, Math.abs(info.mate)));
  }
  if (typeof info.scoreCp === "number") {
    return (info.scoreCp * sign) / 100;
  }
  return null;
}

export function formatMove(move, totalPool) {
  const wins = move.white;
  const losses = move.black;
  const draws = move.draws;
  const total = wins + losses + draws;
  const pool = totalPool || 0;
  const totalText = shortNumber(total);
  const share = pool ? (total / pool) * 100 : 0;
  const winPercentage = total ? (wins / total) * 100 : 0;
  const drawPercentage = total ? (draws / total) * 100 : 0;
  const lossPercentage = total ? (losses / total) * 100 : 0;

  return `
    <div class="move-line">
      <span class="move-text">${move.san}</span>
      <span>${share.toFixed(1)}% • ${totalText} games</span>
    </div>
    <div class="move-subline">
      W ${winPercentage.toFixed(1)}% • D ${drawPercentage.toFixed(1)}% • L ${lossPercentage.toFixed(1)}%
    </div>
  `;
}
